import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { format, subMonths, differenceInMonths, differenceInYears } from "date-fns";
import { formatCurrency } from "@/lib/utils";
import { useQuery } from "@tanstack/react-query";
import { Skeleton } from "@/components/ui/skeleton";

type TimeRange = "6months" | "1year" | "2years";

interface SpendingData {
  month: string;
  planned: number;
  actual: number;
  variance: number;
}

interface SpendingTrendsProps {
  startDate?: Date;
  endDate?: Date;
}

export default function SpendingTrends({
  startDate = subMonths(new Date(), 3),
  endDate = new Date()
}: SpendingTrendsProps) {
  const [timeRange, setTimeRange] = useState<TimeRange>("6months");
  
  // Fetch spending data from API
  const { data: rawSpendingData, isLoading, error } = useQuery<any[]>({
    queryKey: ['/api/analytics/spending-trends', timeRange, startDate, endDate],
    queryFn: async () => {
      const response = await fetch(`/api/analytics/spending-trends?period=${timeRange}&startDate=${startDate.toISOString()}&endDate=${endDate.toISOString()}`);
      if (!response.ok) {
        throw new Error('Failed to fetch spending data');
      }
      return response.json();
    }
  });

  // Transform the data to match the expected format
  const spendingData = rawSpendingData?.map(item => ({
    month: item.period,
    planned: item.planned,
    actual: item.actual,
    variance: item.actual - item.planned
  })) || [];

  const customTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white/95 backdrop-blur-md p-4 border border-slate-200/50 shadow-lg rounded-xl">
          <p className="font-semibold text-slate-800 text-base">{label}</p>
          <div className="mt-2 space-y-2">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-gradient-to-r from-slate-400 to-slate-500"></div>
              <span className="text-sm text-slate-700 font-medium">Right-sized:</span>
              <span className="text-sm font-bold">{formatCurrency(payload[0].value)}</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-gradient-to-r from-blue-500 to-blue-600"></div>
              <span className="text-sm text-slate-700 font-medium">Actual:</span>
              <span className="text-sm font-bold">{formatCurrency(payload[1].value)}</span>
            </div>
            <div className="flex items-center gap-2">
              <div className={`w-3 h-3 rounded-full ${payload[2].value > 0 ? 'bg-gradient-to-r from-red-400 to-red-500' : 'bg-gradient-to-r from-green-400 to-green-500'}`}></div>
              <span className="text-sm text-slate-700 font-medium">Unused spend:</span>
              <span className={`text-sm font-bold ${payload[2].value > 0 ? 'text-red-600' : 'text-green-600'}`}>
                {formatCurrency(payload[2].value)} ({Math.round((payload[2].value / payload[1].value) * 100)}%)
              </span>
            </div>
          </div>
        </div>
      );
    }
    return null;
  };

  // Use the spending data directly
  const chartData = spendingData || [];
  
  // Calculate summary data
  const summaryData = chartData.reduce(
    (acc, curr) => {
      acc.totalPlanned += curr.planned;
      acc.totalActual += curr.actual;
      acc.totalVariance += curr.variance;
      return acc;
    },
    { totalPlanned: 0, totalActual: 0, totalVariance: 0 }
  );

  return (
    <Card>
      <CardHeader className="flex flex-col gap-4">
        <div className="flex flex-row items-center justify-between">
          <CardTitle className="text-lg font-semibold">Spending Trends</CardTitle>
          <div className="flex space-x-2">
            <Button 
              size="sm" 
              variant={timeRange === "6months" ? "default" : "outline"}
              onClick={() => setTimeRange("6months")}
              className="text-xs"
            >
              6 Months
            </Button>
            <Button 
              size="sm" 
              variant={timeRange === "1year" ? "default" : "outline"}
              onClick={() => setTimeRange("1year")}
              className="text-xs"
            >
              1 Year
            </Button>
            <Button 
              size="sm" 
              variant={timeRange === "2years" ? "default" : "outline"}
              onClick={() => setTimeRange("2years")}
              className="text-xs"
            >
              2 Years
            </Button>
          </div>
        </div>
        <div className="text-sm text-muted-foreground">
          <span className="font-medium">Selected period:</span> {format(startDate, 'MMM d, yyyy')} - {format(endDate, 'MMM d, yyyy')}
        </div>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="relative overflow-hidden bg-gradient-to-br from-slate-50 via-slate-100 to-slate-50 p-6 rounded-xl border border-slate-200/60 shadow-sm hover:shadow-md transition-all duration-300">
            <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-br from-slate-200/40 to-transparent rounded-full -translate-y-10 translate-x-10"></div>
            <div className="relative">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-2 h-2 rounded-full bg-gradient-to-r from-slate-400 to-slate-500"></div>
                <p className="text-sm text-slate-600 font-medium">Right-Sized Spend</p>
              </div>
              <p className="text-3xl font-bold text-slate-800">{formatCurrency(summaryData.totalPlanned)}</p>
              <p className="text-xs text-slate-500 mt-2">What you'd pay for only the seats actually assigned</p>
            </div>
          </div>
          
          <div className="relative overflow-hidden bg-gradient-to-br from-blue-50 via-blue-100 to-blue-50 p-6 rounded-xl border border-blue-200/60 shadow-sm hover:shadow-md transition-all duration-300">
            <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-br from-blue-200/40 to-transparent rounded-full -translate-y-10 translate-x-10"></div>
            <div className="relative">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-2 h-2 rounded-full bg-gradient-to-r from-blue-500 to-blue-600"></div>
                <p className="text-sm text-blue-700 font-medium">Actual Spending</p>
              </div>
              <p className="text-3xl font-bold text-blue-800">{formatCurrency(summaryData.totalActual)}</p>
            </div>
          </div>

          <div className={`relative overflow-hidden ${summaryData.totalVariance > 0.5 ? 'bg-gradient-to-br from-red-50 via-red-100 to-red-50 border-red-200/60' : 'bg-gradient-to-br from-green-50 via-green-100 to-green-50 border-green-200/60'} p-6 rounded-xl border shadow-sm hover:shadow-md transition-all duration-300`}>
            <div className={`absolute top-0 right-0 w-20 h-20 ${summaryData.totalVariance > 0.5 ? 'bg-gradient-to-br from-red-200/40' : 'bg-gradient-to-br from-green-200/40'} to-transparent rounded-full -translate-y-10 translate-x-10`}></div>
            <div className="relative">
              <div className="flex items-center gap-2 mb-2">
                <div className={`w-2 h-2 rounded-full ${summaryData.totalVariance > 0.5 ? 'bg-gradient-to-r from-red-400 to-red-500' : 'bg-gradient-to-r from-green-400 to-green-500'}`}></div>
                <p className={`text-sm font-medium ${summaryData.totalVariance > 0.5 ? 'text-red-700' : 'text-green-700'}`}>
                  {summaryData.totalVariance > 0.5 ? 'Unused Spend' : 'Fully Utilized'}
                </p>
              </div>
              <p className={`text-3xl font-bold ${summaryData.totalVariance > 0.5 ? 'text-red-800' : 'text-green-800'}`}>
                {formatCurrency(Math.abs(summaryData.totalVariance))}
              </p>
              <p className="text-xs text-slate-600 mt-2 font-medium">
                {summaryData.totalActual > 0 ? Math.round((summaryData.totalVariance / summaryData.totalActual) * 100) : 0}% of spend going to unassigned seats
              </p>
            </div>
          </div>
        </div>

        <div className="relative bg-gradient-to-br from-slate-50/50 to-white p-6 rounded-xl border border-slate-200/40">
          <div className="h-80">
            {isLoading ? (
              <div className="flex items-center justify-center h-full">
                <div className="space-y-4 w-full">
                  <Skeleton className="h-8 w-32 bg-gradient-to-r from-slate-200 to-slate-300" />
                  <Skeleton className="h-64 w-full bg-gradient-to-r from-slate-200 to-slate-300" />
                </div>
              </div>
            ) : error ? (
              <div className="flex items-center justify-center h-full">
                <div className="text-center p-8">
                  <div className="w-16 h-16 bg-gradient-to-br from-red-100 to-red-200 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                    </svg>
                  </div>
                  <p className="text-red-600 font-medium">Error loading spending data</p>
                  <p className="text-slate-500 text-sm mt-1">Please try refreshing the page</p>
                </div>
              </div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={chartData}
                  margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
                  barCategoryGap="20%"
                >
                  <defs>
                    <linearGradient id="plannedGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#94a3b8" stopOpacity={0.8}/>
                      <stop offset="100%" stopColor="#64748b" stopOpacity={0.6}/>
                    </linearGradient>
                    <linearGradient id="actualGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#3b82f6" stopOpacity={0.9}/>
                      <stop offset="100%" stopColor="#1d4ed8" stopOpacity={0.7}/>
                    </linearGradient>
                    <linearGradient id="varianceGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#ef4444" stopOpacity={0.8}/>
                      <stop offset="100%" stopColor="#dc2626" stopOpacity={0.6}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid 
                    strokeDasharray="2 4" 
                    vertical={false} 
                    stroke="#e2e8f0" 
                    strokeOpacity={0.6}
                  />
                  <XAxis 
                    dataKey="month" 
                    axisLine={false}
                    tickLine={false}
                    tick={{ fontSize: 12, fill: '#64748b', fontWeight: 500 }}
                    dy={10}
                  />
                  <YAxis 
                    axisLine={false}
                    tickLine={false}
                    tick={{ fontSize: 12, fill: '#64748b', fontWeight: 500 }}
                    tickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`}
                    dx={-10}
                  />
                  <Tooltip content={customTooltip} />
                  <Legend 
                    wrapperStyle={{ paddingTop: '20px' }}
                    payload={[
                      {
                        value: 'Right-Sized',
                        type: 'circle',
                        color: '#64748b',
                        formatter: (value) => <span className="text-sm font-medium text-slate-700">{value}</span>
                      },
                      {
                        value: 'Actual',
                        type: 'circle',
                        color: '#3b82f6',
                        formatter: (value) => <span className="text-sm font-medium text-blue-700">{value}</span>
                      },
                      {
                        value: 'Unused Spend',
                        type: 'circle',
                        color: '#ef4444',
                        formatter: (value) => <span className="text-sm font-medium text-red-700">{value}</span>
                      },
                    ]}
                  />
                  <Bar
                    dataKey="planned"
                    name="Right-Sized"
                    fill="url(#plannedGradient)"
                    radius={[4, 4, 0, 0]}
                    stroke="#64748b"
                    strokeWidth={1}
                    strokeOpacity={0.3}
                  />
                  <Bar
                    dataKey="actual"
                    name="Actual"
                    fill="url(#actualGradient)"
                    radius={[4, 4, 0, 0]}
                    stroke="#3b82f6"
                    strokeWidth={1}
                    strokeOpacity={0.3}
                  />
                  <Bar
                    dataKey="variance"
                    name="Unused Spend"
                    fill="url(#varianceGradient)"
                    radius={[4, 4, 0, 0]}
                    stroke="#ef4444"
                    strokeWidth={1}
                    strokeOpacity={0.3}
                  />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
